import { type FC, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import {
  introEaseOut,
  introFadeIn,
  introFadeUp,
  TYPING_SEQUENCE_MS,
} from "../introMotionPresets";
import { IntroBubbleTail } from "../IntroBubbleTail";
import { IntroEdAvatar } from "../IntroEdAvatar";
import { createSignInIntroCopy } from "../signInIntroCopy";
import type { IntroHeroProps } from "../types";
import { useIntroIsActive } from "../IntroActiveIndexContext";
import { usePrefersReducedMotion } from "../usePrefersReducedMotion";
import { useParams } from "next/navigation";

export const IntroSlide1Hero: FC<Omit<IntroHeroProps, "isActive">> = () => {
  const isActive = useIntroIsActive(0);
  const reducedMotion = usePrefersReducedMotion();
  const { locale = "en" } = useParams() as { locale?: string };
  const [phase, setPhase] = useState<"idle" | "typing" | "bubble">("idle");
  const [replayKey, setReplayKey] = useState(0);
  const copy = useMemo(() => createSignInIntroCopy(locale).intro1, [locale]);

  useEffect(() => {
    if (!isActive) {
      setPhase("idle");
      return;
    }

    setReplayKey((k) => k + 1);

    if (reducedMotion) {
      setPhase("bubble");
      return;
    }

    setPhase("typing");
    const bubbleTimer = window.setTimeout(() => {
      setPhase("bubble");
    }, TYPING_SEQUENCE_MS.dotsVisible + TYPING_SEQUENCE_MS.bubbleVisible);

    return () => window.clearTimeout(bubbleTimer);
  }, [isActive, reducedMotion]);

  return (
    <div
      className="flex w-full max-w-[345px] items-start gap-2.5"
      data-testid="intro-slide-1-hero"
      data-node-id="96293:46183"
      key={replayKey}
    >
      <IntroEdAvatar />
      <div className="relative min-w-0 flex-1">
        <AnimatePresence mode="wait">
          {phase === "typing" ? (
            <motion.div
              key="typing"
              data-node-id="96293:46195"
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={introFadeIn}
              transition={introEaseOut}
              className="relative flex flex-col gap-1 rounded-lg bg-content_primary px-4 py-3.5"
            >
              <IntroBubbleTail />
              <div
                className="flex items-center gap-1.5"
                data-node-id="96293:46196"
                aria-hidden
              >
                {[0, 1, 2].map((dot) => (
                  <motion.span
                    key={dot}
                    className="size-2 rounded-full bg-content_inverse_primary"
                    animate={{ opacity: [0.35, 1, 0.35] }}
                    transition={{
                      duration: 0.9,
                      repeat: Infinity,
                      delay: dot * 0.15,
                    }}
                  />
                ))}
              </div>
            </motion.div>
          ) : null}
          {phase === "bubble" ? (
            <motion.div
              key="bubble"
              data-node-id="96293:46192"
              initial="hidden"
              animate="visible"
              variants={introFadeUp}
              transition={introEaseOut}
              className="relative flex flex-col gap-1 rounded-lg bg-content_primary px-4 py-3.5"
            >
              <IntroBubbleTail />
              <p
                className="body_medium_l text-content_inverse_primary whitespace-pre-wrap break-words"
                data-node-id="96293:46194"
              >
                {copy.bubble}
              </p>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </div>
  );
};
